import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  closeWorkspaceDialog,
  useWorkspaceDialog,
  type WorkspaceDialogRequest,
} from '@/lib/assistants/workspace-dialogs';
import { NewProjectDialog } from './new-project-dialog';

/**
 * Gives the focus back to what opened a dialog once it closes (Esc, Cancel, ×, done), as long
 * as the focus was lost with it (M5). The dialog unmounts as it closes, so Radix's own return of
 * the focus does not run.
 */
function useDialogOpenerFocus(request: WorkspaceDialogRequest | null) {
  const opener = useRef<HTMLElement | null>(null);
  const open = request !== null;
  // A layout effect: it runs before the dialog moves the focus into itself.
  useLayoutEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }, [open]);
  useEffect(() => {
    if (open) return;
    const element = opener.current;
    opener.current = null;
    if (element === null) return;
    const frame = requestAnimationFrame(() => {
      const active = document.activeElement;
      const lost = active === null || active === document.body;
      if (lost && element.isConnected && element.closest('[inert]') === null)
        element.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);
}

/**
 * Renders what the sidebar, the menus, ⌘K and the drawer ask with `openWorkspaceDialog`: New
 * assistant (D69). Dialogs on a computer, sheets on a phone. The shell mounts it
 * once.
 */
export function WorkspaceDialogs() {
  const request = useWorkspaceDialog();
  useDialogOpenerFocus(request);
  if (request === null) return null;
  return <NewProjectDialog onClose={closeWorkspaceDialog} />;
}
